<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { debounce } from "lodash-es";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import {
  fileAvatarCreate,
  fileAvatarUpdate,
  fileAvatarDelete,
} from "@/common/api/rest/file/avatar";
import {
  addressList,
  addressCreate,
  addressUpdate,
  addressDelete,
} from "@/common/api/rest/address";
import {
  contactList,
  contactCreate,
  contactUpdate,
  contactDelete,
} from "@/common/api/rest/contact";
import { laboratoryGet, laboratoryUpdate } from "@/common/api/rest/laboratory";

import type { AddressType, ContactType } from "@/common/types/common";
import type {
  LaboratoryType,
  LaboratoryPayloadType,
} from "@/common/types/laboratory";

import { RegisterTheme } from "@/common/themes";
import { addressForm, contactForm, laboratoryForm } from "@/components";
import {
  baseConfirm,
  baseModal,
  baseTable,
  iconMenu,
  svgIcon,
} from "@/common/components";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const addressData = ref<AddressType[]>([]);
const contactData = ref<AddressType[]>([]);

const selectedMenu = ref<string>("home");
const laboratoryData = ref<LaboratoryType | null>(null);
const laboratoryPayload = ref<LaboratoryPayloadType | null>(null);
const addressPayload = ref<AddressType | null>(null);
const contactPayload = ref<ContactType | null>(null);

const selectedAddress = ref<AddressType | null>(null);
const selectedContact = ref<ContactType | null>(null);

const addressEdit = ref(false);
const showModalAddress = ref(false);
const showDelAddress = ref(false);

const contactEdit = ref(false);
const showModalContact = ref(false);
const showDelContact = ref(false);

const showDelAvatar = ref(false);

const accountId = computed(() => route.params.id as string);
const laboratoryId = computed(() => route.params.laboratory as string);

const addressHeader = computed(() => [
  { key: "name", label: t("common.labels.name") },
  { key: "address", label: t("common.labels.address") },
  { key: "cep", label: t("common.labels.cep") },
]);

const contactHeader = computed(() => [
  { key: "name", label: t("common.labels.name") },
  { key: "type", label: t("common.labels.type") },
  { key: "contact", label: t("common.labels.contact") },
]);

const modalAddressTitle = computed(() =>
  addressEdit.value
    ? t("common.labels.edit", {
        concat: t("common.labels.address"),
      }).toUpperCase()
    : t("common.labels.add", {
        concat: t("common.labels.address"),
      }).toUpperCase(),
);

const modalContactTitle = computed(() =>
  addressEdit.value
    ? t("common.labels.edit", {
        concat: t("common.labels.contact"),
      }).toUpperCase()
    : t("common.labels.add", {
        concat: t("common.labels.contact"),
      }).toUpperCase(),
);

const menuTabs = computed(() => [
  {
    key: "home",
    icon: "home-line",
    label: t("common.labels.home"),
  },
  {
    key: "address",
    icon: "pin",
    label: t("common.labels.address"),
  },
  {
    key: "contact",
    icon: "phone",
    label: t("common.labels.contacts"),
  },
]);

function back() {
  router.push(`/${accountId.value}/register/laboratory`);
}

function fetchLaboratory() {
  laboratoryGet(accountId.value, laboratoryId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        laboratoryData.value = data;

        laboratoryPayload.value = {
          name: data.name,
          fantasy_name: data.fantasy_name,
          description: data.description,
          document_name: data.document_name,
          document_number: data.document_number,
          document_type: data.document_type,
        };
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function updateLaboratory(data: LaboratoryPayloadType) {
  laboratoryUpdate(accountId.value, laboratoryId.value, data)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchLaboratory();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

// ADDRESS

function fetchAddress() {
  addressList(accountId.value, laboratoryId.value, {
    page: 0,
    limit: 100,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        addressData.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function addAddress() {
  addressEdit.value = false;
  showModalAddress.value = true;
}

function addressCancel() {
  addressEdit.value = false;
  showModalAddress.value = false;
  addressPayload.value = null;
}

function addAddressConfirm() {
  if (!addressPayload.value) return;

  store.commit("SET_LOADING", true);

  addressCreate(accountId.value, laboratoryId.value, addressPayload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchAddress();
        addressCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function updateAddress(data: AddressType) {
  selectedAddress.value = data;
  addressPayload.value = data;
  showModalAddress.value = true;
  addressEdit.value = true;
}

function updateAddressConfirm() {
  if (!selectedAddress.value) return;

  store.commit("SET_LOADING", true);

  addressUpdate(
    accountId.value,
    selectedAddress.value.address_id as string,
    addressPayload.value as AddressType,
  )
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchAddress();
        addressCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function submitAddress() {
  if (!addressEdit.value) addAddressConfirm();
  else updateAddressConfirm();
}

function removeAddress(data: AddressType) {
  selectedAddress.value = data;
  showDelAddress.value = true;
}

function removeAddressCancel() {
  selectedAddress.value = null;
  showDelAddress.value = false;
}

function removeAddressConfirm() {
  if (!selectedAddress.value) return;

  store.commit("SET_LOADING", true);

  addressDelete(accountId.value, selectedAddress.value.address_id as string)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchLaboratory();
        removeAddressCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

// CONTACT

function fetchContact() {
  contactList(accountId.value, laboratoryId.value, {
    page: 0,
    limit: 100,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        contactData.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function addContact() {
  showModalContact.value = true;
}

function contactCancel() {
  contactEdit.value = false;
  showModalContact.value = false;
  contactPayload.value = null;
}

function addContactConfirm() {
  if (!contactPayload.value) return;

  store.commit("SET_LOADING", true);

  contactCreate(accountId.value, laboratoryId.value, contactPayload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchContact();
        contactCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function updatedContact(data: ContactType) {
  contactEdit.value = true;
  contactPayload.value = data;
  selectedContact.value = data;
  showModalContact.value = true;
}

function updatedContactConfirm() {
  if (!contactPayload.value && !selectedContact.value) return;

  store.commit("SET_LOADING", true);

  contactUpdate(
    accountId.value,
    selectedContact.value?.contact_id as string,
    contactPayload.value as ContactType,
  )
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchContact();
        contactCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function submitContact() {
  if (!contactEdit.value) addContactConfirm();
  else updatedContactConfirm();
}

function removeContact(data: ContactType) {
  selectedContact.value = data;
  showDelContact.value = true;
}

function removeContactCancel() {
  selectedContact.value = null;
  showDelContact.value = false;
}

function removeContactConfirm() {
  if (!selectedContact.value) return;

  store.commit("SET_LOADING", true);

  contactDelete(accountId.value, selectedContact.value?.contact_id as string)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchContact();
        removeContactCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

// AVATAR

function addAvatar(avatar: string) {
  fileAvatarCreate(accountId.value, {
    owner_id: laboratoryId.value,
    type: "LABORATORY",
    file: avatar,
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchLaboratory();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function updateAvatar(avatar: string) {
  if (!laboratoryData.value?.avatar) return;

  fileAvatarUpdate(accountId.value, laboratoryData.value.avatar.avatar_id, {
    file: avatar,
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchLaboratory();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function changeAvatar(avatar: string) {
  if (laboratoryData.value?.avatar) {
    updateAvatar(avatar);
  } else {
    addAvatar(avatar);
  }
}

function delAvatar() {
  showDelAvatar.value = true;
}

function delAvatarCancel() {
  showDelAvatar.value = false;
}

function delAvatarConfirm() {
  if (!laboratoryData.value?.avatar) return;

  fileAvatarDelete(accountId.value, laboratoryData.value!.avatar.avatar_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchLaboratory();
        showDelAvatar.value = false;
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

const debounceForm = debounce(updateLaboratory, 1000);

watchEffect(() => {
  if (laboratoryId.value) {
    if (selectedMenu.value === "home") fetchLaboratory();
    else if (selectedMenu.value === "address") fetchAddress();
    else if (selectedMenu.value === "contact") fetchContact();
  }
});
</script>

<template>
  <register-theme tab-active="laboratory">
    <template #title>
      <div class="flex flex-row justify-between items-center w-full">
        <div class="flex flex-row gap-1 items-center">
          <svg-icon icon="laboratory" size="2rem" />
          {{
            t("common.labels.edit", { concat: t("common.labels.laboratory") })
          }}
        </div>

        <button type="button" class="btn btn-back btn-sm" @click="back()">
          <svg-icon icon="close" size="1.25rem" />
        </button>
      </div>
    </template>

    <template #content>
      <icon-menu
        :tabs="menuTabs"
        :active="selectedMenu"
        @selected="selectedMenu = $event"
      >
        <template #content>
          <template v-if="selectedMenu === 'home'">
            <div class="title">
              {{ t("common.labels.laboratory") }}
            </div>
            <laboratory-form
              width="100%"
              :data="laboratoryPayload"
              :show-avatar="true"
              :avatar="laboratoryData?.avatar?.small"
              @update-data="debounceForm"
              @avatar-add="changeAvatar"
              @avatar-del="delAvatar()"
            />
          </template>

          <template v-if="selectedMenu === 'address'">
            <div class="title">
              {{ t("common.labels.address") }}
            </div>

            <base-table
              :header="addressHeader"
              :data="addressData"
              :paginated="false"
              :show-search="false"
              :options="true"
              :edit="true"
              :del="true"
              @btn:add="addAddress()"
              @btn:edit="updateAddress"
              @btn:del="removeAddress"
            >
              <template #address="{ data }">
                {{ data.city }} - {{ data.state }}
              </template>
            </base-table>
          </template>

          <template v-if="selectedMenu === 'contact'">
            <div class="title">
              {{ t("common.labels.contact") }}
            </div>

            <base-table
              :header="contactHeader"
              :data="contactData"
              :paginated="false"
              :show-search="false"
              :options="true"
              :edit="true"
              :del="true"
              @btn:add="addContact()"
              @btn:edit="updatedContact"
              @btn:del="removeContact"
            >
              <template #type="{ data }">
                {{ t(`common.labels.${data.type}`) }}
              </template>
            </base-table>
          </template>
        </template>
      </icon-menu>
    </template>
  </register-theme>

  <base-modal
    :show="showModalAddress"
    :title="modalAddressTitle"
    :show-button-confirm="true"
    :button-confirm="
      addressEdit ? t('common.labels.save') : t('common.labels.add')
    "
    :button-close="t('common.labels.cancel')"
    @close="addressCancel()"
    @confirm="submitAddress()"
  >
    <template #content>
      <address-form
        :data="addressPayload"
        @update-data="addressPayload = $event"
      />
    </template>
  </base-modal>

  <base-confirm
    :show="showDelAddress"
    :title="t('common.labels.remove', { concat: t('common.labels.company') })"
    :message="
      t('common.texts.remove_message', { concat: selectedAddress?.name })
    "
    @cancel="removeAddressCancel"
    @confirm="removeAddressConfirm()"
  />

  <base-modal
    :show="showModalContact"
    :title="modalContactTitle"
    :show-button-confirm="true"
    :button-confirm="
      contactEdit ? t('common.labels.save') : t('common.labels.add')
    "
    :button-close="t('common.labels.cancel')"
    @close="contactCancel()"
    @confirm="submitContact()"
  >
    <template #content>
      <contact-form
        :data="contactPayload"
        @update-data="contactPayload = $event"
      />
    </template>
  </base-modal>

  <base-confirm
    :show="showDelContact"
    :title="t('common.labels.remove', { concat: t('common.labels.contact') })"
    :message="
      t('common.texts.remove_message', { concat: selectedContact?.name })
    "
    @cancel="removeContactCancel"
    @confirm="removeContactConfirm()"
  />

  <base-confirm
    :show="showDelAvatar"
    :title="t('common.labels.remove', { concat: t('common.labels.avatar') })"
    :message="
      t('common.texts.remove_message', { concat: t('common.labels.avatar') })
    "
    @cancel="delAvatarCancel"
    @confirm="delAvatarConfirm()"
  />
</template>

<style lang="scss" scoped>
.title {
  display: flex;
  flex-direction: column;
  padding-inline: 0.5rem;
  border-bottom: 1px solid var(--border);
  margin-block: 2rem;
  padding-block-end: 0.5rem;
  font-size: 1.15rem;
  font-weight: bold;
  text-transform: uppercase;
}
</style>
